import { useEffect, useState, useRef, useMemo } from "react"; // استيراد useMemo للتجميع الفعال
import API from "../../Api/axiosConfig";
import { toast } from "react-toastify";
import MyInput from "../../Components/Myinput";
import Mytitle from "../../Components/Mytitle";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPrint } from "@fortawesome/free-solid-svg-icons";
import { useSettings } from "../../context/SettingsContext";
import { useTranslation } from "react-i18next";
import { FloatingLabel, Form } from "react-bootstrap";
import { toLocal } from "../../utils/toLocal";
import IncomePrint from "./IncomePrint";
import { useReactToPrint } from "react-to-print"; 

function IncomeStatement() {
  const { t, i18n } = useTranslation();
  const { settings } = useSettings();

  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [branches, setBranches] = useState([]);
  const [filters, setFilters] = useState({
    from: "",
    to: "",
    branch_uuid: "",
  });

  const componentRef = useRef(null);

  const handlePrint = useReactToPrint({
    contentRef: componentRef,
    documentTitle: t("income_statement"),
    pageStyle: `
      @page { size: auto; margin: 20mm; }
      @media print {
        body, html, .print-section-wrapper, .report-card, .income-statement-container {
          background-color: #ffffff !important;
          color: #000000 !important;
        }
        .report-card {
          background: #ffffff !important;
          box-shadow: none !important;
          border: none !important;
        }
        h5, span, strong, div, p { color: #000000 !important; }
        .expense-details-item { border-bottom: 1px dotted #ccc !important; }
        * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
      }
    `,
  });

  // دالة ذكية لتجميع المصروفات بناءً على الـ expense_id وحساب الفترات الزمنية
  const groupedExpenses = useMemo(() => {
    if (!report?.minvoices?.data) return [];

    const groups = {};

    report.minvoices.data.forEach((item) => {
      const id = item.expense_id;
      const amount = parseFloat(item.amount) || 0;
      const date = item.date;
      const name = item.expense?.name || t("unknown_expense");

      if (!groups[id]) {
        groups[id] = {
          name: name,
          totalAmount: 0,
          dates: [],
        };
      }

      groups[id].totalAmount += amount;
      if (date) groups[id].dates.push(new Date(date));
    });

    // تحويل الكائن إلى مصفوفة مع صياغة النص "من فترة ... إلى ..."
    return Object.values(groups).map((group) => {
      let periodText = "";
      if (group.dates.length > 0) {
        // ترتيب التواريخ لمعرفة أول وآخر تاريخ
        const sortedDates = group.dates.sort((a, b) => a - b);
        const firstDate = sortedDates[0].toISOString().split('T')[0];
        const lastDate = sortedDates[sortedDates.length - 1].toISOString().split('T')[0];
        
        // لو تكرر في نفس اليوم نكتب التاريخ مرة واحدة، لو أيام مختلفة نكتب الفترة
        periodText = firstDate === lastDate 
          ? `${t("date")}: ${firstDate}`
          : `${t("income_from")} ${firstDate} ${t("income_to")} ${lastDate}`;
      }

      return {
        name: group.name,
        amount: group.totalAmount,
        period: periodText,
      };
    });
  }, [report, t]);

  useEffect(() => {
    API.get("/branches")
      .then((res) => setBranches(res.data.data || []))
      .catch((err) => console.error("Failed to load branches", err));
  }, []);

  const fetchReport = async (body = filters) => {
    setLoading(true);
    try {
      const { data } = await API.post("/income-statement", body);
      setReport(data.data);
    } catch (error) {
      toast.error(t("income_load_error"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
    const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);

    const formatDate = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    };

    const initialFilters = {
      from: formatDate(firstDay),
      to: formatDate(lastDay),
      branch_uuid: "",
    };

    setFilters(initialFilters);
    fetchReport(initialFilters);
  }, []);

  return (
    <>
      <div className="income-statement-container">
        <Mytitle title={t("income_title")} className="no-print" />

        <div className="filters row g-3 mb-4 no-print">
          <div className="col-md-3">
            <MyInput
              label={t("income_from")}
              type="date"
              value={filters.from}
              onChange={(e) => {
                const nextFilters = { ...filters, from: e.target.value };
                setFilters(nextFilters);
                fetchReport(nextFilters);
              }}
              dontShowDay={true}
            />
          </div>
          <div className="col-md-3">
            <MyInput
              label={t("income_to")}
              type="date"
              value={filters.to}
              onChange={(e) => {
                const nextFilters = { ...filters, to: e.target.value };
                setFilters(nextFilters);
                fetchReport(nextFilters);
              }}
              dontShowDay={true}
            />
          </div>
          <div className="col-md-3">
            <FloatingLabel label={t("branch")}>
              <Form.Select
                className="form-select mb-3"
                style={{ height: "38px" }}
                value={filters.branch_uuid}
                onChange={(e) => {
                  const nextFilters = { ...filters, branch_uuid: e.target.value };
                  setFilters(nextFilters);
                  fetchReport(nextFilters);
                }}
              >
                <option value="">{t("select_branch")}</option>
                {branches.map((opt, j) => (
                  <option key={j} value={opt.uuid}>{opt.name}</option>
                ))}
              </Form.Select>
            </FloatingLabel>
          </div>
        </div>

        <div className="no-print align-self-end mb-3">
          <button className="btn btn-outline-info" onClick={handlePrint}>
            <FontAwesomeIcon icon={faPrint} />
          </button>
        </div>

        <div ref={componentRef} className="print-section-wrapper">
          <IncomePrint i18n={i18n} settings={settings} t={t}>
            {report && (
              <div style={{ direction: i18n.dir() }} className="report-card shadow-sm p-4 rounded">
                <h5 className="mb-3 text-center">
                  {t("income_from")} {report.from} {t("income_to")} {report.to}
                </h5>

                <div className="report-row">
                  <span>{t("income_revenue")}</span>
                  <strong>{toLocal(report.invoices.total)}</strong>
                </div>

                <div className="report-row">
                  <span>{t("income_service_cost")}</span>
                  <strong>{toLocal(report.invoices.total - report.invoices.total_win)}</strong>
                </div>

                {/* البند الرئيسي للمصروفات */}
                <div className="report-row mb-1">
                  <span>{t("income_expenses")}</span>
                  <strong>{toLocal(report.minvoices.total)}</strong>
                </div>

                {/* عرض المصروفات المجمّعة بدون تكرار الاسم */}
                {groupedExpenses.length > 0 && (
                  <div 
                    className="expense-details-container mb-3" 
                    style={{ 
                      paddingRight: i18n.dir() === "rtl" ? "20px" : "0px", 
                      paddingLeft: i18n.dir() === "ltr" ? "20px" : "0px",
                      borderRight: i18n.dir() === "rtl" ? "2px solid #e0e0e0" : "none",
                      borderLeft: i18n.dir() === "ltr" ? "2px solid #e0e0e0" : "none"
                    }}
                  >
                    {groupedExpenses.map((exp, index) => (
                      <div 
                        key={index} 
                        className="expense-details-item d-flex justify-content-between text-muted py-1"
                        style={{ fontSize: "0.9rem", borderBottom: "1px dashed #eee" }}
                      >
                        <span>
                          {exp.name} 
                          <small className="mx-2 text-secondary" style={{ fontSize: "0.8rem" }}>
                            ({exp.period})
                          </small>
                        </span>
                        <span>{toLocal(exp.amount)}</span>
                      </div>
                    ))}
                  </div>
                )}

                <div className="report-row">
                  <span>{t("income_salaries")}</span>
                  <strong>{toLocal(report.salary.total)}</strong>
                </div>

                <div className="report-row net pt-2 mt-2">
                  <span>{t("income_net_profit")}</span>
                  <strong>{toLocal(report.net_income)}</strong>
                </div>
              </div>
            )}
          </IncomePrint>
        </div>
      </div>
    </>
  );
}

export default IncomeStatement;